import React, { useState } from "react";
import {
  Card,
  CardContent,
  IconButton,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Button,
  TextField,
  CircularProgress,
  FormHelperText,
  Box,
} from "@mui/material";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";

const StudentPromotion = () => {
  const [loading, setLoading] = useState(false); // For loading state
  const [uploadedFile, setUploadedFile] = useState(null);
  const navigate = useNavigate();

  const yearRange = [];
  for (let year = 2017; year <= 2024; year++) {
    yearRange.push(`${year}-${year + 1}`);
  }

  // Yup validation schema
  const validationSchema = Yup.object({
    admissionNumber: Yup.string().required("Admission number is required"),
    studentName: Yup.string().required("Student name is required"),
    currentSession: Yup.string().required("Current session is required"),
    promoteSession: Yup.string().required("Promote session is required"),
    fromClass: Yup.string().required("From class is required"),
    toClass: Yup.string().required("To class is required"),
    file: Yup.mixed()
      .required("File upload is required")
      .test(
        "fileFormat",
        "Unsupported file format. Please upload a valid document.",
        (value) =>
          !value ||
          ["application/pdf", "image/jpeg", "image/png"].includes(value.type)
      ),
  });

  // Formik for form management
  const formik = useFormik({
    initialValues: {
      admissionNumber: "",
      studentName: "",
      currentSession: "",
      promoteSession: "",
      fromClass: "",
      toClass: "",
      file: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Prepare data for submission
        const formData = new FormData();
        formData.append("admissionNumber", values.admissionNumber);
        formData.append("studentName", values.studentName);
        formData.append("currentSession", values.currentSession);
        formData.append("promoteSession", values.promoteSession);
        formData.append("fromClass", values.fromClass);
        formData.append("toClass", values.toClass);
        formData.append("file", values.file);

        // API call (mocked as a POST request)
        const response = await fetch(
          "https://your-backend-api-url.com/transfer",
          {
            method: "POST",
            body: formData,
          }
        );

        if (response.ok) {
          console.log("Student promoted successfully");
          formik.resetForm();
          setUploadedFile(null);
        } else {
          console.error("Failed to promote student");
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Card
      style={{
        // margin: "60px 20px",
        marginRight: "20px",
        marginTop: "70px",
        marginLeft: "20px",
        padding: "20px",
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        borderRadius: "8px",
        width: "97%",
      }}
    >
      <CardContent>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Box display="flex" alignItems="center" sx={{ marginBottom: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: "#2e2a54" }}>
              <KeyboardBackspaceIcon fontSize="medium" />
            </IconButton>
            <Typography variant="h4" component="h3" sx={{ fontWeight: "bold" }}>
              Student Promotion
            </Typography>
          </Box>
        </div>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={2}>
            {/* Admission Number */}
            <Grid item xs={6}>
              <TextField
                label="Admission Number"
                variant="outlined"
                fullWidth
                id="admissionNumber"
                name="admissionNumber"
                value={formik.values.admissionNumber}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.admissionNumber &&
                  Boolean(formik.errors.admissionNumber)
                }
                helperText={
                  formik.touched.admissionNumber &&
                  formik.errors.admissionNumber
                }
              />
            </Grid>

            {/* Student Name */}
            <Grid item xs={6}>
              <TextField
                label="Student Name"
                variant="outlined"
                fullWidth
                id="studentName"
                name="studentName"
                value={formik.values.studentName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.studentName &&
                  Boolean(formik.errors.studentName)
                }
                helperText={
                  formik.touched.studentName && formik.errors.studentName
                }
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} style={{ marginTop: "20px" }}>
            {/* Current Session */}
            <Grid item xs={3}>
              <FormControl
                fullWidth
                error={
                  formik.touched.currentSession &&
                  Boolean(formik.errors.currentSession)
                }
              >
                <Select
                  id="currentSession"
                  name="currentSession"
                  displayEmpty
                  value={formik.values.currentSession}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="" disabled>
                    Select Year
                  </MenuItem>
                  {yearRange.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.currentSession &&
                  formik.errors.currentSession && (
                    <FormHelperText>
                      {formik.errors.currentSession}
                    </FormHelperText>
                  )}
              </FormControl>
            </Grid>

            {/* Promote Session */}
            <Grid item xs={3}>
              <FormControl
                fullWidth
                error={
                  formik.touched.promoteSession &&
                  Boolean(formik.errors.promoteSession)
                }
              >
                <Select
                  id="promoteSession"
                  name="promoteSession"
                  displayEmpty
                  value={formik.values.promoteSession}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="" disabled>
                    Select Year
                  </MenuItem>
                  {yearRange.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.promoteSession &&
                  formik.errors.promoteSession && (
                    <FormHelperText>
                      {formik.errors.promoteSession}
                    </FormHelperText>
                  )}
              </FormControl>
            </Grid>

            {/* From Class */}
            <Grid item xs={3}>
              <FormControl
                fullWidth
                error={
                  formik.touched.fromClass && Boolean(formik.errors.fromClass)
                }
              >
                <Select
                  id="fromClass"
                  name="fromClass"
                  displayEmpty
                  value={formik.values.fromClass}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="" disabled>
                    Select Class
                  </MenuItem>
                  <MenuItem value="Nursery">Nursery</MenuItem>
                  <MenuItem value="LKG">LKG</MenuItem>
                  <MenuItem value="UKG">UKG</MenuItem>
                  <MenuItem value="Class 1">Class 1</MenuItem>
                  <MenuItem value="Class 2">Class 2</MenuItem>
                  <MenuItem value="Class 3">Class 3</MenuItem>
                  <MenuItem value="Class 4">Class 4</MenuItem>
                  <MenuItem value="Class 5">Class 5</MenuItem>
                  <MenuItem value="Class 6">Class 6</MenuItem>
                  <MenuItem value="Class 7">Class 7</MenuItem>
                  <MenuItem value="Class 8">Class 8</MenuItem>
                  <MenuItem value="Class 9">Class 9</MenuItem>
                  <MenuItem value="Class 10">Class 10</MenuItem>
                </Select>
                {formik.touched.fromClass && formik.errors.fromClass && (
                  <FormHelperText>{formik.errors.fromClass}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* To Class */}
            <Grid item xs={3}>
              <FormControl
                fullWidth
                error={formik.touched.toClass && Boolean(formik.errors.toClass)}
              >
                <Select
                  id="toClass"
                  name="toClass"
                  displayEmpty
                  value={formik.values.toClass}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="" disabled>
                    Select Class
                  </MenuItem>
                  <MenuItem value="Class 1">Class 1</MenuItem>
                  <MenuItem value="Class 2">Class 2</MenuItem>
                  <MenuItem value="Class 3">Class 3</MenuItem>
                  <MenuItem value="Class 4">Class 4</MenuItem>
                  <MenuItem value="Class 5">Class 5</MenuItem>
                  <MenuItem value="Class 6">Class 6</MenuItem>
                  <MenuItem value="Class 7">Class 7</MenuItem>
                  <MenuItem value="Class 8">Class 8</MenuItem>
                  <MenuItem value="Class 9">Class 9</MenuItem>
                  <MenuItem value="Class 10">Class 10</MenuItem>
                </Select>
                {formik.touched.toClass && formik.errors.toClass && (
                  <FormHelperText>{formik.errors.toClass}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </Grid>

          {/* Submit Button */}
          <Grid
            container
            spacing={2}
            style={{ marginTop: "20px" }}
            justifyContent="flex-end"
          >
            <Grid item>
              <Button
                variant="contained"
                color="secondary" // Change the color here (e.g., 'secondary' for a different color)
                type="submit"
                disabled={loading}
                style={{
                  padding: "10px 20px",
                  fontSize: "13px",
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Submit"
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default StudentPromotion;